import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { EMPTY, Subscription, catchError, delay, filter, finalize, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { StaffService } from '../../services/staff.service';
import { RolesService } from '@app/pages/home-page/shop-settings/settings-roles/services/roles.service';
import { RoleOption } from '@app/pages/home-page/shop-settings/settings-roles/interfaces/role-response.interface';
import { StaffData, StaffUpdatePayload } from '@app/utils/interfaces/staff.interface';
import { ErrorModalComponent } from '@app/components/shared/error-modal/error-modal.component';
import { ConfirmModalComponent } from '@app/components/shared/confirm-modal/confirm-modal.component';
import { CONFIRM_DIALOG_OPTIONS } from '@app/utils/constants/confirm.constants';
import { IconName } from '@app/utils/enums/icon-enum';
import { ICON_PATH } from '@app/utils/constants/icon-map.constants';
import {
  BaseEditForm,
  ValidatorsForForms,
} from '@app/components/shared/base-edit-form/base-edit-form';
import { Router } from '@angular/router';
import { AuthService } from '@app/services/auth/auth.service';
import { InfoMessageModalComponent } from '@app/components/shared/info-message-modal/info-message-modal.component';
import { InfoMessageModalData } from '@app/utils/interfaces/info-message-modal-data.interface';
import { PASSWORD_SUCCESS_MODAL_CONFIG } from '@app/utils/constants/password-reset-success-modal.constants';

@Component({
  selector: 'app-staff-edit-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatDialogModule,
    IconComponent,
    DatePipe,
  ],
  templateUrl: './staff-edit-form.component.html',
  styleUrls: ['./staff-edit-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaffEditFormComponent extends BaseEditForm implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<StaffEditFormComponent>);
  private readonly staffService = inject(StaffService);
  private readonly rolesService = inject(RolesService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly data = inject<StaffData>(MAT_DIALOG_DATA);
  private readonly dialog = inject(MatDialog);

  private readonly rolesPageNumber = 1;
  private readonly rolesPageSize = 10;

  private logoutSubscription?: Subscription;
  public readonly IconName = IconName;
  public readonly icons = ICON_PATH;
  public readonly loading = signal(false);
  public readonly passwordUpdateDate = signal(this.data.staff.passwordUpdatedAt);
  public readonly isResettingPassword = signal(false);
  public roles: RoleOption[] = [];

  public readonly validationErrors: Record<string, ValidatorsForForms[]> = {
    firstName: [
      { key: 'required', translation: 'staff.edit.errors.firstNameInvalid' },
      { key: 'minlength', translation: 'staff.edit.errors.firstNameInvalid' },
      { key: 'maxlength', translation: 'staff.edit.errors.firstNameInvalid' },
      { key: 'pattern', translation: 'staff.edit.errors.firstNameInvalid' },
    ],
    lastName: [
      { key: 'required', translation: 'staff.edit.errors.lastNameInvalid' },
      { key: 'minlength', translation: 'staff.edit.errors.lastNameInvalid' },
      { key: 'maxlength', translation: 'staff.edit.errors.lastNameInvalid' },
      { key: 'pattern', translation: 'staff.edit.errors.lastNameInvalid' },
    ],
    email: [
      { key: 'required', translation: 'staff.edit.errors.emailInvalid' },
      { key: 'email', translation: 'staff.edit.errors.emailInvalid' },
      { key: 'maxlength', translation: 'staff.edit.errors.emailInvalid' },
    ],
    role: [{ key: 'required', translation: 'staff.edit.errors.roleInvalid' }],
  };

  public form: FormGroup = this.fb.group({
    firstName: ['', this.getNameValidators()],
    lastName: ['', this.getNameValidators()],
    email: ['', this.getEmailValidators()],
    role: [{ value: '', disabled: true }, Validators.required],
  });

  public ngOnInit(): void {
    this.patchInitialFormValue();
    this.loadRoles();
  }

  public getErrorMessageForStaff(controlName: string): string {
    return super.getErrorMessage(this.form, controlName, this.validationErrors);
  }

  protected onSave(): void {
    if (this.loading()) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const selectedRole = this.getSelectedRole();

    if (!selectedRole) {
      this.openErrorModal('staff.edit.errors.roleInvalid');
      return;
    }

    this.loading.set(true);

    this.staffService
      .updateStaffPersonalInfo(this.data.staff.id, this.buildStaffPayload(selectedRole.id))
      .pipe(
        tap(() => this.dialogRef.close(true)),
        catchError((err) => {
          this.handleSaveError(err);

          return EMPTY;
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected onCancel(): void {
    if (!this.hasChanges()) {
      this.dialogRef.close();
      return;
    }

    this.dialogRef.addPanelClass('hidden-dialog');

    this.openConfirmDialog()
      .afterClosed()
      .pipe(
        tap((confirmed) => this.handleCancelConfirmation(confirmed)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public removeStaffMember(): void {
    if (this.loading()) {
      return;
    }

    this.openConfirmDialog()
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.staffService.removeStaff(this.data.staff.id).pipe(
            tap(() => {
              this.openErrorModal('staff.edit.errors.accountRemoved');
              this.dialogRef.close(true);
              if (this.data.staff.id === this.data.activeUserId) {
                this.logoutWithDelay(10000);
              }
            }),
            catchError(() => {
              this.openErrorModal('staff.edit.errors.system');
              return EMPTY;
            }),
            finalize(() => this.loading.set(false)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public logoutWithDelay(delayMs: number): void {
    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
    }

    this.logoutSubscription = of(null)
      .pipe(
        delay(delayMs),
        tap(() => {
          this.authService.logout();
          this.router.navigateByUrl('');
        }),
      )
      .subscribe({
        complete: () => (this.logoutSubscription = undefined),
      });
  }

  public resetPassword(): void {
    if (this.isResettingPassword()) {
      return;
    }

    this.isResettingPassword.set(true);
    this.loading.set(true);

    this.staffService
      .resetStaffPassword(this.data.staff.id)
      .pipe(
        catchError(() => {
          this.openErrorModal('staff.edit.errors.system');
          return EMPTY;
        }),
        finalize(() => {
          this.loading.set(false);
          this.isResettingPassword.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.dialogRef.close();
        this.openPasswordGenerationModal(PASSWORD_SUCCESS_MODAL_CONFIG);
      });
  }

  private openPasswordGenerationModal(data: InfoMessageModalData): void {
    this.dialog.open(InfoMessageModalComponent, {
      panelClass: 'info-message-modal',
      data,
    });
  }

  private patchInitialFormValue(): void {
    this.form.patchValue({
      firstName: this.data.staff.firstName ?? '',
      lastName: this.data.staff.lastName ?? '',
      email: this.data.staff.email ?? '',
      role: this.data.staff.role ?? '',
    });
  }

  private loadRoles(): void {
    this.loading.set(true);

    this.rolesService
      .getRoles(this.rolesPageNumber, this.rolesPageSize)
      .pipe(
        tap((res) => this.handleRolesLoaded(res.data.items ?? [])),
        catchError(() => {
          this.openErrorModal('staff.edit.errors.system');

          return EMPTY;
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private handleRolesLoaded(roles: RoleOption[]): void {
    this.roles = roles;

    const roleControl = this.form.get('role');

    roleControl?.enable();

    if (this.data.staff.role) {
      roleControl?.setValue(this.data.staff.role);
    }
  }

  private getSelectedRole(): RoleOption | undefined {
    const { role } = this.form.getRawValue();

    return this.roles.find((roleOption) => roleOption.name === role);
  }

  private buildStaffPayload(roleId: number): StaffUpdatePayload {
    const raw = this.form.getRawValue();

    return {
      firstName: raw.firstName,
      lastName: raw.lastName,
      email: raw.email,
      roleId,
    };
  }

  private handleSaveError(err: unknown): void {
    const errors = (err as { error?: { errors?: Record<string, string[]> } })?.error?.errors ?? {};
    const message = this.mapBackendErrors(errors);

    this.dialogRef.close(false);
    this.openErrorModal(message);
  }

  private handleCancelConfirmation(confirmed: boolean | undefined): void {
    if (confirmed) {
      this.dialogRef.close();
      return;
    }

    this.dialogRef.removePanelClass('hidden-dialog');
  }

  private openConfirmDialog(): MatDialogRef<ConfirmModalComponent, boolean> {
    return this.dialog.open<ConfirmModalComponent, unknown, boolean>(ConfirmModalComponent, {
      ...CONFIRM_DIALOG_OPTIONS,
      panelClass: 'confirm-dialog',
    });
  }

  private openErrorModal(message: string): void {
    this.dialog.open(ErrorModalComponent, {
      width: '400px',
      data: {
        message,
      },
    });
  }

  private mapBackendErrors(errors: Record<string, string[]>): string {
    const errorMap: Record<string, string> = {
      email: 'staff.edit.errors.emailInvalid',
      firstname: 'staff.edit.errors.firstNameInvalid',
      lastname: 'staff.edit.errors.lastNameInvalid',
      role: 'staff.edit.errors.roleInvalid',
    };

    const firstKey = Object.keys(errors)[0]?.toLowerCase();

    return firstKey
      ? (errorMap[firstKey] ?? 'staff.edit.errors.system')
      : 'staff.edit.errors.system';
  }

  private hasChanges(): boolean {
    const raw = this.form.getRawValue();

    return (
      raw.firstName !== (this.data.staff.firstName ?? '') ||
      raw.lastName !== (this.data.staff.lastName ?? '') ||
      raw.email !== (this.data.staff.email ?? '') ||
      raw.role !== (this.data.staff.role ?? '')
    );
  }
}
